import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import {MiniMergeStatusBoundary} from './MiniMergeStatusBoundary'
import type {MiniStatusIconProps} from './MiniStatusIcon'

import {useSyncFavicon} from '@github-ui/mergebox/use-sync-favicon'
import {useGoToMergeBox} from '../../../mergebox/use-go-to-mergebox'
import {useMergeabilityData} from '../use-mergeability-data'
import {isSuccessStatus, type CombinedState} from '@github-ui/mergebox/page-data/payloads/status-checks'
import {projectChecksProgress} from '../checks-progress-projection'
import {MultiColorProgressBar} from './MultiColorProgressBar'
import {isForYouRefreshEnabled} from '../../shared/for-you-feature-flags'

const iconTypeByCombinedState: Record<CombinedState, MiniStatusIconProps['type']> = {
  PASSED: 'success',
  PENDING: 'warning',
  PENDING_APPROVAL: 'warning',
  PENDING_CONFLICTS: 'warning',
  FAILED: 'failure',
  PENDING_FAILED: 'failure',
  SOME_FAILED: 'failure',
}

export function ChecksMiniStatusBoundary({children}: {children: React.ReactNode}) {
  const goToMergeBox = useGoToMergeBox()
  const isRefreshEnabled = isForYouRefreshEnabled()

  return (
    <MiniMergeStatusBoundary
      action={goToMergeBox}
      icon={ChevronDownIcon}
      actionLabel="View checks"
      boundaryName="ChecksMiniStatusBoundary"
      isFirst={isRefreshEnabled}
      text="Checks"
    >
      {children}
    </MiniMergeStatusBoundary>
  )
}

export function ChecksMiniStatus({expanded, onToggle}: {expanded?: boolean; onToggle: () => void}) {
  const data = useMergeabilityData()
  useSyncFavicon(data?.statusRollup, {isInMergeQueue: data?.mergeBoxPullRequest.isInMergeQueue})

  const isRefreshEnabled = isForYouRefreshEnabled()
  const projection = projectChecksProgress(data?.statusRollup, data?.statusChecks, data?.checksSection.sectionStatus)

  const rollupSummary = data?.statusRollup.summary || []
  let legacySuccessfulChecks = 0
  let legacyTotalChecks = 0

  for (const summary of rollupSummary) {
    legacyTotalChecks += summary.count

    if (isSuccessStatus(summary.state)) {
      legacySuccessfulChecks += summary.count
    }
  }

  const pendingWorkflowCount = data?.statusRollup.pendingWorkflowApprovalRollup?.workflowsRequiringApprovalCount ?? 0
  const activeStateMinimum = data && data.statusRollup.combinedState !== 'PASSED' ? 1 : 0
  legacyTotalChecks = Math.max(legacyTotalChecks, pendingWorkflowCount, activeStateMinimum)

  const legacyIsUnavailable = Boolean(data) && legacyTotalChecks === 0
  const legacyIcon = data ? iconTypeByCombinedState[data.statusRollup.combinedState] : 'error'
  const legacyStatus = data ? undefined : 'Checks unavailable'

  const successful = isRefreshEnabled ? projection.successful : legacySuccessfulChecks
  const total = isRefreshEnabled ? projection.total : legacyTotalChecks
  const isUnavailable = isRefreshEnabled
    ? projection.isUnavailable && projection.icon === 'success'
    : legacyIsUnavailable
  const icon = isRefreshEnabled ? projection.icon : legacyIcon
  const status = isRefreshEnabled
    ? (projection.status ?? (projection.isUnavailable ? 'No checks available' : undefined))
    : legacyStatus
  const actionLabel = expanded ? 'Collapse checks' : 'Expand checks'
  const arrowIcon = expanded ? ChevronDownIcon : ChevronRightIcon
  const unavailableLabel = projection.status ?? 'No checks available'

  return (
    <MiniMergeStatusItem
      aria-label={actionLabel}
      aria-expanded={expanded}
      isFirst={isRefreshEnabled}
      onClick={onToggle}
    >
      <MiniMergeStatusItem.LabelRow icon={arrowIcon} summary={isRefreshEnabled ? projection.summary : undefined}>
        <MiniMergeStatusItem.Label
          icon={icon}
          isUnavailable={isUnavailable}
          status={status}
          successful={successful}
          total={total}
        >
          Checks
        </MiniMergeStatusItem.Label>
      </MiniMergeStatusItem.LabelRow>
      {isRefreshEnabled ? (
        <MultiColorProgressBar
          aria-label={projection.progressLabel}
          aria-valuetext={projection.progressValueText}
          blockingSummary={projection.blockingSummary}
          clearedBlockingSummary="Checks status updated"
          max={projection.total}
          miniStatusSpacing
          semantics="breakdown"
          state={
            projection.isUnavailable
              ? {kind: 'unavailable', label: unavailableLabel}
              : {kind: 'available', segments: projection.segments}
          }
          value={projection.successful}
        />
      ) : (
        <MiniMergeStatusItem.Progress
          aria-label={`${successful} of ${total} successful checks`}
          isUnavailable={legacyIsUnavailable}
          successful={successful}
          total={total}
          unavailableLabel="No checks available"
        />
      )}
    </MiniMergeStatusItem>
  )
}
